// ---- Enums ----

export enum AssessmentStatus {
  PENDING = "PENDING",
  ACTIVE = "ACTIVE",
  SCAN_FAILED = "SCAN_FAILED",
  ARCHIVED = "ARCHIVED",
}

export enum AssessmentFileAction {
  UPLOAD = "UPLOAD",
  DOWNLOAD = "DOWNLOAD",
}

export type AssessmentUploadStatus =
  | "idle"
  | "creating"
  | "subscribing"
  | "uploading"
  | "scanning"
  | "active"
  | "failed";

// ---- Types ----

export interface AssessmentPlan {
  planId: string;
  status: AssessmentStatus;
  name: string;
  title: string;
  provider: string;
  uri: string;
  contentType: string;
  fileKey?: string;
  fileSizeBytes?: number;
  createdBy: string;
  createdAt: string;
  updatedAt?: string;
  archivedAt?: string;
  archivedBy?: string;
}

export interface AssessmentPlanEvent {
  planId: string;
  provider: string;
  oldStatus: AssessmentStatus;
  newStatus: AssessmentStatus;
  name: string;
  title: string;
  updatedAt: string;
}

// ---- Inputs ----

export interface CreateAssessmentPlanInput {
  name: string;
  title: string;
  provider: string;
  uri: string;
  contentType: string;
}

export interface ListAssessmentPlansInput {
  limit?: number;
  nextToken?: string;
}

export interface ArchiveAssessmentPlanInput {
  planId: string;
}

export interface RequestAssessmentPlanUrlInput {
  action: AssessmentFileAction;
  planId: string;
}

// ---- Responses ----

export interface CreateAssessmentPlanResponse {
  createAssessmentPlan: {
    plan: AssessmentPlan;
    uploadUrl: string;
    uploadKey: string;
    uploadExpiresAt: string;
    uploadHeaders: string | null;
  };
}

export interface ListAssessmentPlansResponse {
  listAssessmentPlans: {
    items: AssessmentPlan[];
    nextToken?: string;
  };
}

export interface GetAssessmentPlanResponse {
  getAssessmentPlan: AssessmentPlan;
}

export interface RequestAssessmentPlanUrlResponse {
  requestAssessmentPlanUrl: {
    plan: Pick<AssessmentPlan, "planId" | "title" | "status">;
    url: string;
    expiresAt: string;
    headers: string | null;
  };
}

// ---- Assessments Domain Queries & Mutations ----

export const CREATE_ASSESSMENT_PLAN = `
  mutation CreateAssessmentPlan($input: CreateAssessmentPlanInput!) {
    createAssessmentPlan(input: $input) {
      plan {
        planId
        status
        name
        title
        provider
        uri
        contentType
        createdBy
        createdAt
      }
      uploadUrl
      uploadKey
      uploadExpiresAt
      uploadHeaders
    }
  }
`;

export const LIST_ASSESSMENT_PLANS = `
  query ListAssessmentPlans($input: ListAssessmentPlansInput) {
    listAssessmentPlans(input: $input) {
      items {
        planId
        status
        name
        title
        provider
        uri
        contentType
        fileKey
        fileSizeBytes
        createdBy
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

export const GET_ASSESSMENT_PLAN = `
  query GetAssessmentPlan($planId: ID!) {
    getAssessmentPlan(planId: $planId) {
      planId
      status
      name
      title
      provider
      uri
      contentType
      fileKey
      fileSizeBytes
      createdBy
      createdAt
      updatedAt
      archivedAt
      archivedBy
    }
  }
`;

export const REQUEST_ASSESSMENT_PLAN_URL = `
  mutation RequestAssessmentPlanUrl($input: RequestAssessmentPlanUrlInput!) {
    requestAssessmentPlanUrl(input: $input) {
      plan {
        planId
        title
        status
      }
      url
      expiresAt
      headers
    }
  }
`;

export const ARCHIVE_ASSESSMENT_PLAN = `
  mutation ArchiveAssessmentPlan($input: ArchiveAssessmentPlanInput!) {
    archiveAssessmentPlan(input: $input) {
      planId
      status
      archivedAt
      archivedBy
    }
  }
`;

// ---- Assessments Domain Subscription ----

export const ON_PLAN_EVENT = `
  subscription OnPlanEvent($planId: String) {
    onPlanEvent(planId: $planId) {
      planId
      provider
      oldStatus
      newStatus
      name
      title
      updatedAt
    }
  }
`;
